import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ForbiddenError, HttpClient } from '../../api/HttpClient';
import { CanvasSnapshot, RetroRoomResponse, StickyState, TextLabelState } from '../../api/Requests';
import { useAlert } from '../../contexts/alert/useAlert';
import { AuthState, UserContext } from '../../contexts/UserContext';
import CanvasSurface from './canvas/CanvasSurface';
import { RetroCategory, RetroAction } from './analytics/RetroTracking';
import { useTracking } from '../../analytics/useTracking';

export interface RetroRoomProps {
  roomUrlId: string;
  playerId: string;
  playerName: string;
}

type LoadingState = 'loading' | 'ready' | 'error' | 'closed';

export default function RetroRoom({ roomUrlId, playerId, playerName }: RetroRoomProps): ReactElement {
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [room, setRoom] = useState<RetroRoomResponse | null>(null);
  const [initialStickies, setInitialStickies] = useState<StickyState[]>([]);
  const [initialTextLabels, setInitialTextLabels] = useState<TextLabelState[]>([]);
  const alert = useAlert();
  const { authState, authToken, user } = useContext(UserContext);
  const { track } = useTracking();

  // Bind the user's account to their player slot whenever auth state becomes SignedIn,
  // covering both sign-in and sign-up while already inside the room.
  //
  // Three dependencies mirror the poker enrollModeratorPowers pattern:
  //   authState       — fires as soon as Firebase confirms the identity
  //   authToken       — fires once the JWT is available (may lag authState by one tick)
  //   user?.plan?.status — fires when the backend /users/signin response arrives and the
  //                        user object is fully populated (important for sign-up, where the
  //                        trial plan is created after Firebase auth completes)
  useEffect(() => {
    if (authState !== AuthState.SignedIn || !authToken) return;

    new HttpClient(authToken)
      .post(`/retro-rooms/${roomUrlId}/enroll`, { playerId })
      .then(() => {
        track.event(RetroCategory.Room, RetroAction.PlayerEnrolled);
      })
      .catch(() => {
        // Enrollment is best-effort; ignore failures silently so the user stays in the room.
      });
  }, [authState, authToken, user?.plan?.status, roomUrlId, playerId, track]);

  useEffect(() => {
    const httpClient = new HttpClient(authToken);
    // Pass playerId so that locked rooms can verify the requester was a prior participant.
    const params = `?playerId=${encodeURIComponent(playerId)}`;

    Promise.all([
      httpClient.get(`/retro-rooms/${roomUrlId}${params}`) as Promise<RetroRoomResponse>,
      httpClient.get(`/retro-rooms/${roomUrlId}/canvas${params}`) as Promise<CanvasSnapshot>,
    ])
      .then(([roomData, canvas]) => {
        setRoom(roomData);
        setInitialStickies(canvas.stickies ?? []);
        setInitialTextLabels(canvas.textLabels ?? []);
        setLoadingState('ready');
      })
      .catch((e) => {
        if (e instanceof ForbiddenError) {
          setLoadingState('closed');
          return;
        }
        alert.showMessage('Could not load retro room', 'error');
        setLoadingState('error');
      });
  }, [roomUrlId, authToken, playerId]);

  if (loadingState === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadingState === 'closed') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, height: '80vh', px: 3 }}>
        <Typography variant="h5" align="center">This retrospective is closed</Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ maxWidth: 480 }}>
          It is no longer accessible from this device. If you signed in during the session you can
          reopen it from your recent retros.
        </Typography>
      </Box>
    );
  }

  if (loadingState === 'error' || !room) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Typography color="error">Failed to load retro room</Typography>
      </Box>
    );
  }

  return (
    <CanvasSurface
      roomUrlId={roomUrlId}
      playerId={playerId}
      playerName={playerName}
      isModerator={room.players.find(p => p.id === playerId)?.isModerator ?? false}
      initialRoomState={room.state}
      initialTemplateId={room.template ?? null}
      initialStickies={initialStickies}
      initialTextLabels={initialTextLabels}
    />
  );
}
